"use client"
import {
  EmptyState,
  Media,
  SideSheet,
  SideSheetBody,
  SideSheetHeader,
  transitions,
} from "@opensea/ui-kit"
import { useMountEffect } from "@opensea/ui-kit/hooks"
import { Close } from "@opensea/ui-kit/icons"
import { MotionConfig } from "motion/react"
import { ErrorBoundary, type FallbackProps } from "react-error-boundary"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../components/Button"
import { useTrackError } from "../../providers/TrackErrorProvider"
import { FlowActions, FlowState } from "../../stores/flowStore"
import type { ViewWalletFlowState } from "../flow-state"
import { useIsFlowActive } from "../hooks/useIsFlowActive"
import { DepositFlowContent } from "./DepositFlow/DepositFlowContent"
import { FiatCheckoutFlowContent } from "./FiatCheckoutFlow/FiatCheckoutFlowContent"
import { SendProvider } from "./SendFlow/provider/SendProvider"
import { SendFlowContent } from "./SendFlow/SendFlowContent"
import { SwapFlow } from "./SwapFlow/SwapFlow"
import { TopupFlowContent } from "./TopupFlow/TopupFlowContent"
import type { ViewWalletTab } from "./ViewWalletFlow/types"
import { ViewWalletFlowContent } from "./ViewWalletFlow/ViewWalletFlowContent"

export const SideSheetFlows = () => {
  const { flow } = useSnapshot(FlowState)

  const renderContent = () => {
    switch (flow?.type) {
      case "view-wallet":
        return (
          <ViewWalletFlowContent
            address={flow.address}
            switchTab={(tab: ViewWalletTab) => {
              FlowActions.setFlowField<ViewWalletFlowState>("tab", tab)
            }}
            tab={flow.tab}
          />
        )
      case "send":
        return (
          <SendProvider>
            <MotionConfig transition={transitions.default}>
              <SendFlowContent onBack={flow.onBack} />
            </MotionConfig>
          </SendProvider>
        )
      case "swap":
        return (
          <SwapFlow
            initialFromCurrency={flow.initialFromCurrency}
            initialToCurrency={flow.initialToCurrency}
            onBack={flow.onBack}
            onClose={FlowActions.endFlow}
          />
        )
      case "deposit":
        return <DepositFlowContent onBack={flow.onBack} />
      case "topup":
        return <TopupFlowContent onBack={flow.onBack} />
      case "fiat-checkout":
        return (
          <FiatCheckoutFlowContent
            item={flow.item}
            listingId={flow.listingId}
            onBack={flow.onBack}
            onTransactionCreated={flow.onTransactionCreated}
          />
        )
      default:
        return null
    }
  }

  const isViewWalletActive = useIsFlowActive("view-wallet")
  const isSendActive = useIsFlowActive("send")
  const isSwapActive = useIsFlowActive("swap")
  const isDepositActive = useIsFlowActive("deposit")
  const isTopupActive = useIsFlowActive("topup")
  const isFiatCheckoutActive = useIsFlowActive("fiat-checkout")
  const isSideSheetFlowActive =
    isViewWalletActive ||
    isSendActive ||
    isSwapActive ||
    isDepositActive ||
    isTopupActive ||
    isFiatCheckoutActive

  return (
    <>
      <Media greaterThanOrEqual="md">
        <SideSheet
          content={
            <SideSheetErrorBoundary resetKeys={[flow?.type]}>
              {renderContent()}
            </SideSheetErrorBoundary>
          }
          direction="right"
          onOpenChange={open => {
            if (!open) {
              FlowActions.endFlow()
            }
          }}
          open={isSideSheetFlowActive}
        />
      </Media>
      <Media lessThan="md">
        <SideSheet
          content={
            <SideSheetErrorBoundary resetKeys={[flow?.type]}>
              {renderContent()}
            </SideSheetErrorBoundary>
          }
          direction="right"
          onOpenChange={open => {
            if (!open) {
              FlowActions.endFlow()
            }
          }}
          open={isSideSheetFlowActive}
          withCloseIcon={isViewWalletActive}
        />
      </Media>
    </>
  )
}

const SideSheetErrorFallback = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const t = useTranslations("wallet.SideSheetFlows")
  const trackError = useTrackError()

  useMountEffect(() => {
    trackError(error, {
      tags: {
        flow: FlowState.flow?.type ?? "",
        source: "SideSheetErrorFallback",
      },
    })
  })

  return (
    <>
      <SideSheetHeader>
        <Media lessThan="md">
          <Button
            className="ml-auto h-8 p-1.5"
            onClick={() => {
              FlowActions.endFlow()
            }}
            variant="ghost"
          >
            <Close size={20} />
          </Button>
        </Media>
      </SideSheetHeader>
      <SideSheetBody className="h-full">
        <EmptyState
          description={t("description")}
          extra={
            <Button onClick={resetErrorBoundary} variant="secondary">
              {t("reset")}
            </Button>
          }
          size="lg"
          title={t("title")}
          variant="no-data"
        />
      </SideSheetBody>
    </>
  )
}

const SideSheetErrorBoundary = ({
  children,
  resetKeys,
}: {
  children: React.ReactNode
  resetKeys?: unknown[]
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={SideSheetErrorFallback}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  )
}
